import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { formatAmount } from "@/utils/formatAmount";
import { useCreateRequisition } from "@/features/orders/hooks/useRequisitions";

interface ProjectOpt { id: string; name: string; short_name: string; is_active: boolean; }

interface DraftLine {
  custom_name: string;
  custom_unit: string;
  quantity: string;
  custom_unit_rate: string;
}

const emptyLine = (): DraftLine => ({ custom_name: "", custom_unit: "szt.", quantity: "1", custom_unit_rate: "" });

const lineTotal = (l: DraftLine) => (parseFloat(l.quantity) || 0) * (parseFloat(l.custom_unit_rate) || 0);

/**
 * Szybki szkic zapotrzebowania — ręczne pozycje (nazwa, jednostka, ilość, stawka).
 * Najbardziej miękka warstwa kosztów podwykonawczych: wstępny szacunek, do uściślenia
 * przez zatwierdzone zapotrzebowanie / zamówienie / fakturę. Tworzony jako DRAFT.
 */
export function QuickDraftRequisitionModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const create = useCreateRequisition();
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => apiClient.get<{ data: ProjectOpt[] }>("/projects/").then((r) => r.data.data),
  });

  const [project, setProject] = useState("");
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [err, setErr] = useState("");

  const projectOptions = useMemo(
    () =>
      projects
        .filter((p) => p.is_active)
        .map((p) => ({ value: p.id, label: `${p.short_name} — ${p.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [projects],
  );

  const grandTotal = lines.reduce((s, l) => s + lineTotal(l), 0);

  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const save = () => {
    setErr("");
    if (!project) { setErr("Wybierz przedsięwzięcie."); return; }
    if (description.trim().length < 10) { setErr("Opis musi mieć co najmniej 10 znaków."); return; }
    const valid = lines.filter((l) => l.custom_name.trim());
    if (valid.length === 0) { setErr("Dodaj co najmniej jedną pozycję z nazwą."); return; }
    for (const l of valid) {
      if (!l.custom_unit.trim()) { setErr(`Pozycja „${l.custom_name}" — podaj jednostkę.`); return; }
      if (!(parseFloat(l.quantity) > 0)) { setErr(`Pozycja „${l.custom_name}" — ilość musi być > 0.`); return; }
      if (!(parseFloat(l.custom_unit_rate) > 0)) { setErr(`Pozycja „${l.custom_name}" — stawka musi być > 0.`); return; }
    }
    const payload = {
      project,
      description: description.trim(),
      priority: "standard",
      deadline: null,
      line_items: valid.map((l) => ({
        line_item_type: null,
        custom_name: l.custom_name.trim(),
        custom_unit: l.custom_unit.trim(),
        custom_unit_rate: parseFloat(l.custom_unit_rate),
        quantity: parseFloat(l.quantity),
        description: "",
        attachment_required: false,
        required_file_extension: "",
      })),
    };
    create.mutate(payload, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["contractor-layers"] });
        qc.invalidateQueries({ queryKey: ["salary-reconciliation"] });
        onClose();
      },
      onError: () => setErr("Nie udało się zapisać szkicu. Sprawdź dane i uprawnienia."),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-5 w-[44rem] max-w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h2 className="font-bold text-gray-900 dark:text-gray-100">Szybki szkic zapotrzebowania</h2>
            <p className="text-xs text-gray-500">Wstępny szacunek kosztu podwykonawcy — ręczne pozycje. Zapisze się jako szkic (najmniej pewna warstwa).</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={18} /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Przedsięwzięcie</label>
            <SearchableSelect
              options={projectOptions}
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder="— wybierz przedsięwzięcie —"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Opis</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="np. Szacunek robót ziemnych — wstępny"
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
        </div>

        {/* Pozycje */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Pozycje</h3>
            <Button type="button" size="sm" variant="secondary" onClick={() => setLines((p) => [...p, emptyLine()])}>
              <Plus size={14} className="mr-1" /> Dodaj pozycję
            </Button>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_5rem_4rem_6rem_6rem_2rem] gap-2 text-[10px] uppercase tracking-wider text-gray-400 px-1">
              <span>Nazwa</span><span>Jedn.</span><span className="text-right">Ilość</span><span className="text-right">Stawka</span><span className="text-right">Wartość</span><span />
            </div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_5rem_4rem_6rem_6rem_2rem] gap-2 items-center">
                <input
                  value={l.custom_name}
                  onChange={(e) => setLine(i, { custom_name: e.target.value })}
                  placeholder="Nazwa pozycji"
                  className="rounded border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                />
                <input
                  value={l.custom_unit}
                  onChange={(e) => setLine(i, { custom_unit: e.target.value })}
                  className="rounded border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                />
                <input
                  type="number" min="0" step="any"
                  value={l.quantity}
                  onChange={(e) => setLine(i, { quantity: e.target.value })}
                  className="rounded border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-sm text-right bg-white dark:bg-gray-800 dark:text-gray-100"
                />
                <input
                  type="number" min="0" step="any"
                  value={l.custom_unit_rate}
                  onChange={(e) => setLine(i, { custom_unit_rate: e.target.value })}
                  placeholder="zł"
                  className="rounded border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-sm text-right bg-white dark:bg-gray-800 dark:text-gray-100"
                />
                <div className="text-right tabular-nums text-sm text-gray-700 dark:text-gray-200">{formatAmount(lineTotal(l).toFixed(2))}</div>
                <button
                  onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}
                  disabled={lines.length === 1}
                  className="text-gray-300 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Usuń pozycję"
                ><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-3 text-sm">
            <span className="text-gray-500 mr-2">Razem szacunek:</span>
            <span className="font-bold tabular-nums text-gray-900 dark:text-gray-100">{formatAmount(grandTotal.toFixed(2))} PLN</span>
          </div>
        </div>

        {err && <p className="text-sm text-red-600 mt-3">{err}</p>}

        <div className="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button loading={create.isPending} onClick={save}>Zapisz szkic</Button>
        </div>
      </div>
    </div>
  );
}
